/**
 * Coluna de contexto da ficha: próxima ação, Leitura do Analista (Central),
 * datas-chave, pedidos e pessoas. Desktop: recolhível (vira um botão de
 * 44 px e a grade anima a largura). Celular e tablet: carrossel com snap.
 */
import React from "react";
import { motion } from "framer-motion";
import { FiSidebar } from "react-icons/fi";

import { plural } from "../../../../utils/dinheiro";
import IconeDaIA from "../../../CentralProjetos/IconeDaIA";
import { BarraCrescente } from "../comum/Graficos";
import { ENTRADA_DE_CARD, transicaoDeCard } from "../comum/movimento";
import { iniciais } from "../dados/ficha";
import { ROTULO_DA_ESPERA } from "../dados/regras";

const FRASE_DA_ESPERA = {
  equipe: "O cliente espera a equipe",
  cliente: "A equipe espera o cliente",
  ninguem: "Ninguém esperando",
  indefinido: "Não dá para dizer",
};
const COR_DO_PAPEL = { vendas: "var(--c2)", RC: "var(--accent)", projeto: "var(--ok)", empreita: "var(--warn)", "": "var(--warn)" };

function Card({ indice, className = "", rotulo, children }) {
  return (
    <motion.section className={`cl-card ${className}`} aria-label={rotulo}
      initial={ENTRADA_DE_CARD.inicial} animate={ENTRADA_DE_CARD.final} transition={transicaoDeCard(indice, 0.15)}>
      {children}
    </motion.section>
  );
}

function quando(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hoje = new Date();
  const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === hoje.toDateString()) return `hoje ${hora}`;
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${hora}`;
}

export function ProximaAcao({ acao, aoAgir }) {
  return (
    <Card indice={0} className={`cl-nx ${acao.tom}`} rotulo="Próxima ação">
      <div className="cl-eyebrow">Próxima ação{acao.quando ? ` · ${acao.quando}` : ""}</div>
      <div className="cl-nx-t">{acao.titulo}</div>
      {acao.descricao && <p className="cl-nx-d">{acao.descricao}</p>}
      {acao.botao && (
        <button type="button" className="cl-btn sm" onClick={() => aoAgir(acao.acao)}>{acao.botao}</button>
      )}
    </Card>
  );
}

export function LeituraDoAnalista({ central }) {
  const c = central.cliente;
  const urgencia = c.urgencia == null ? null : Math.round(c.urgencia * 100);
  const semResposta = central.semRespostaDesde;
  return (
    <Card indice={1} className="cl-jev" rotulo="Leitura do Analista">
      <div className="cl-card-h" style={{ marginBottom: 10 }}>
        <div className="cl-jev-b">
          <span className="cl-jev-i" aria-hidden="true"><IconeDaIA tamanho={15} /></span>
          <div className="cl-jev-t">Leitura do Analista<small>conversas + planilha</small></div>
        </div>
      </div>
      <p className="cl-jev-sum">{c.resumo || "Ainda sem leitura das conversas deste cliente."}</p>
      {urgencia != null && (
        <div className="cl-urg">
          <span className="cl-eyebrow" style={{ flex: "none" }}>Urgência</span>
          <span className="cl-urg-t" role="img" aria-label={`Urgência ${urgencia}%`}>
            <BarraCrescente proporcao={urgencia / 100} atraso={0.3} style={{ background: "linear-gradient(90deg, var(--warn), var(--bad))" }} />
          </span>
          <b className="cl-mono" style={{ fontSize: 12 }}>{urgencia}%</b>
        </div>
      )}
      <div className="cl-kv">
        <div className="cl-kv-i">Quem espera<b className={`sans ${c.quem_espera}`}>{FRASE_DA_ESPERA[c.quem_espera] || ROTULO_DA_ESPERA[c.quem_espera]}</b></div>
        <div className="cl-kv-i">Última mensagem<b>{quando(c.ultima_mensagem_em)}</b></div>
        {semResposta && <div className="cl-kv-i">Sem resposta desde<b>{quando(semResposta)}</b></div>}
      </div>
    </Card>
  );
}

export function DatasChave({ datas }) {
  return (
    <Card indice={2} rotulo="Datas-chave">
      <div className="cl-eyebrow" style={{ marginBottom: 8 }}>Datas-chave</div>
      <div className="cl-kv">
        {datas.map((d) => <div key={d.rotulo} className="cl-kv-i">{d.rotulo}<b className={d.tom || undefined}>{d.valor}</b></div>)}
      </div>
    </Card>
  );
}

function Pessoa({ nome, papel }) {
  return (
    <span className="cl-pp" title={papel ? `${nome} · ${papel}` : nome}>
      <i style={{ background: COR_DO_PAPEL[papel] || "var(--c2)" }} aria-hidden="true">{iniciais(nome)}</i>
      <span>{nome}</span>
      {papel && <em>{papel}</em>}
    </span>
  );
}

function marcaDoPedido(pedido) {
  if (pedido.totalEmAndamento === 0) return { tom: "ok", texto: "entregue" };
  if (pedido.totalDelayDays > 0) return { tom: "bad", texto: `${pedido.totalDelayDays} d atraso` };
  return { tom: "ok", texto: "em dia" };
}

export function PedidosEPessoas({ cliente, pessoas, pedidoAtivo, aoIrParaPedido }) {
  return (
    <Card indice={3} rotulo="Pedidos e pessoas">
      <div className="cl-grp">
        <div className="cl-eyebrow">Pedidos</div>
        <div className="cl-ped-sel">
          {cliente.pedidos.map((p) => {
            const marca = marcaDoPedido(p);
            const total = Math.max(1, p.totalAmbientes);
            return (
              <button key={p.id} type="button" className="cl-ps" aria-current={pedidoAtivo === p.id}
                onClick={() => aoIrParaPedido(p.id)}>
                <span className="cl-ps-t">
                  <span>Pedido {p.numero || p.label}</span>
                  <span className={`cl-pill sm p-${marca.tom}`}><span>{marca.texto}</span></span>
                </span>
                <span className="cl-ps-s">
                  {plural(p.totalAmbientes, "amb.", "amb.")} · {p.totalEmAndamento} em andamento · {p.totalEntregues} entregues
                </span>
                <span className="cl-prog" aria-hidden="true">
                  {p.totalEntregues > 0 && <i className="cl-seg-e" style={{ flex: p.totalEntregues / total }} />}
                  {p.totalEmAndamento > 0 && <i className="cl-seg-n" style={{ flex: p.totalEmAndamento / total }} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {pessoas.equipe.length > 0 && (
        <div className="cl-grp">
          <div className="cl-eyebrow">Pessoas</div>
          <div className="cl-ppl">{pessoas.equipe.map((p) => <Pessoa key={`${p.papel}-${p.nome}`} {...p} />)}</div>
        </div>
      )}
      {pessoas.marceneiros.length > 0 && (
        <div className="cl-grp">
          <div className="cl-eyebrow">Marceneiros</div>
          <div className="cl-ppl">{pessoas.marceneiros.map((p) => <Pessoa key={p.nome} {...p} />)}</div>
        </div>
      )}
    </Card>
  );
}

export function BotaoRecolher({ recolhido, aoAlternar }) {
  const rotulo = recolhido ? "Mostrar painel" : "Ocultar painel";
  return (
    <button type="button" className="cl-side-t" aria-expanded={!recolhido} aria-label={rotulo} title={rotulo} onClick={aoAlternar}>
      <FiSidebar size={16} aria-hidden="true" />
      <span className="cl-side-tl">{rotulo}</span>
    </button>
  );
}
